import type {Employee, PeopleSearchRankingMode} from 'client';
import {useCallback, useEffect, useRef, useState} from 'react';

import type {SearchResultMetadata} from './globalSearchAnalytics';
import {
  PeopleTypeaheadAnalyticsSession,
  type PeopleTypeaheadContext,
} from './peopleTypeaheadAnalytics';

type SearchableEmployee = Employee & {search_metadata?: SearchResultMetadata};

export const usePeopleTypeaheadAnalytics = ({
  context,
  input,
  results,
  isLoading,
  rankingMode,
  visibleResultCount = 4,
}: {
  context: PeopleTypeaheadContext;
  input: string;
  results: SearchableEmployee[];
  isLoading: boolean;
  rankingMode: PeopleSearchRankingMode;
  visibleResultCount?: number;
}) => {
  const sessionRef = useRef<PeopleTypeaheadAnalyticsSession>();
  const requestStartedAtRef = useRef(performance.now());
  const previousRequestRef = useRef<string>();
  const currentRequestIdRef = useRef<string>();
  const selectionMethodRef = useRef<'click' | 'keyboard'>('click');
  const selectionTrackedRef = useRef(false);
  const restartOnNextInputRef = useRef(false);
  const previousInputRef = useRef(input);
  const [sessionVersion, setSessionVersion] = useState(0);
  const visibleResultCountRef = useRef(visibleResultCount);
  const impressedResultCountRef = useRef(0);
  visibleResultCountRef.current = visibleResultCount;

  const clearSession = useCallback(() => {
    sessionRef.current = undefined;
    previousRequestRef.current = undefined;
    currentRequestIdRef.current = undefined;
    impressedResultCountRef.current = 0;
    selectionTrackedRef.current = false;
    selectionMethodRef.current = 'click';
  }, []);

  const start = useCallback(() => {
    if (!sessionRef.current) {
      sessionRef.current = new PeopleTypeaheadAnalyticsSession(context, rankingMode);
      requestStartedAtRef.current = performance.now();
      restartOnNextInputRef.current = false;
      // A picker can reopen with its previous query and results still rendered.
      // Advance the lifecycle so that existing rows are recorded for this new session.
      setSessionVersion((version) => version + 1);
    }
  }, [context, rankingMode]);

  useEffect(() => {
    requestStartedAtRef.current = performance.now();
  }, [input]);

  useEffect(() => {
    const inputChanged = previousInputRef.current !== input;
    previousInputRef.current = input;
    if (inputChanged && input && restartOnNextInputRef.current) {
      start();
    }
  }, [input, start]);

  useEffect(() => {
    if (!input || isLoading || !sessionRef.current) return;
    // The raw query is used only for local de-duplication and never logged.
    const requestSignature = `${input}\u0000${results.map((result) => result.ldap).join('\u0001')}`;
    if (previousRequestRef.current === requestSignature) return;
    previousRequestRef.current = requestSignature;

    const visibleResults = results
      .slice(0, visibleResultCountRef.current)
      .map((employee, rank) => ({
        localIdentity: employee.ldap,
        rank,
        metadata: employee.search_metadata,
      }));
    const requestId = sessionRef.current.resultsViewed({
      query: input,
      resultCount: results.length,
      shownTopK: visibleResults.length,
      elapsedMs: performance.now() - requestStartedAtRef.current,
      rankingVariant: rankingMode,
    });
    currentRequestIdRef.current = requestId;
    sessionRef.current.resultsImpressed(requestId, visibleResults);
    impressedResultCountRef.current = visibleResults.length;
  }, [input, isLoading, rankingMode, results, sessionVersion]);

  useEffect(() => {
    const requestId = currentRequestIdRef.current;
    const previouslyImpressed = impressedResultCountRef.current;
    if (!requestId || visibleResultCount <= previouslyImpressed) return;

    const newlyVisibleResults = results
      .slice(previouslyImpressed, visibleResultCount)
      .map((employee, rank) => ({
        localIdentity: employee.ldap,
        rank: rank + previouslyImpressed,
        metadata: employee.search_metadata,
      }));
    sessionRef.current?.resultsImpressed(requestId, newlyVisibleResults);
    impressedResultCountRef.current += newlyVisibleResults.length;
  }, [results, visibleResultCount]);

  useEffect(
    () => () => {
      sessionRef.current?.end('route_change');
    },
    []
  );

  const markKeyboardSelection = useCallback(() => {
    selectionMethodRef.current = 'keyboard';
  }, []);

  const clearSelectionMethod = useCallback(() => {
    selectionMethodRef.current = 'click';
  }, []);

  const select = useCallback(
    (employee: SearchableEmployee, selectionMethod?: 'click' | 'keyboard') => {
      const rank = results.findIndex((result) => result.ldap === employee.ldap);
      if (isLoading || rank < 0 || !currentRequestIdRef.current) return false;
      sessionRef.current?.resultSelected({
        requestId: currentRequestIdRef.current,
        result: {localIdentity: employee.ldap, rank, metadata: employee.search_metadata},
        selectionMethod: selectionMethod ?? selectionMethodRef.current,
        rankingVariant: rankingMode,
      });
      selectionTrackedRef.current = true;
      selectionMethodRef.current = 'click';
      return true;
    },
    [isLoading, rankingMode, results]
  );

  const commit = useCallback(() => {
    // A local fallback row can remain visible while a typed request is loading.
    // It is deliberately outside this typed-query metric, so it cannot qualify
    // a session unless its rendered row first produced a selection event.
    if (!selectionTrackedRef.current) return;
    sessionRef.current?.selectionCommitted();
    clearSession();
    restartOnNextInputRef.current = true;
  }, [clearSession]);

  const dismiss = useCallback(() => {
    sessionRef.current?.end('dismissed');
    clearSession();
  }, [clearSession]);

  const showMore = useCallback(() => sessionRef.current?.showMore(), []);

  return {start, markKeyboardSelection, clearSelectionMethod, select, commit, dismiss, showMore};
};
